import React, { FormEvent, useContext, useEffect, useState } from "react";
import { ATTICUS_CONSUMER_APP_HOST, ATTICUS_CONSUMER_APP_HOST_ORIGINAL, SCREEN_PATHS } from "../../../constants";
import { Box, Button, Checkbox, Theme, Typography } from "@mui/material";
import { FormDialog } from "../../shared/FormDialog";
import { ProbateField } from "../../../models/ProbateField";
import { RenderEditableField } from "./RenderEditableField";
import {
  ProbateDetails,
  ProbateFieldCategory,
} from "../../../models/ProbateDetails";
import { useNavigate } from "react-router";
import { ScreenContentWrapper } from "../../shared/ScreenContentWrapper";
import { probateApi } from "../../../apis";
import { Loading } from "../../shared/Loading";
import { LeadProbateContext } from "LeadProbateContextWrapper";
import { LeadContext } from "LeadContextWrapper";
import { GeographyContext } from "GeographyContextWrapper";

export const LeadProbateReviewScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { saveFieldAnswered, getProbateDetails } = useContext(LeadProbateContext);
  const { countries, states } = React.useContext(GeographyContext)
  const { lead, leadData } = useContext(LeadContext)
  const [errors, setErrors] = useState<string | string[]>();
  const [confirmationError, setConfirmationError] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState<ProbateField[]>();
  const [probateDetails, setProbateDetails] = useState<ProbateDetails | undefined>();
  const [questionComponent, setQuestionComponent] = useState<JSX.Element>();

  useEffect(() => {
    const getDetails = async () => {
      if (!getProbateDetails) {
        return
      }
      if (probateDetails?.probateType) {
        return
      }

      const probateDetailResponse = await getProbateDetails();
      setProbateDetails(probateDetailResponse);
    }
    getDetails()
  }, [lead?.key, leadData, getProbateDetails]);

  useEffect(() => {
    // check completeness when probate details changes so that the UI updates to highlight incomplete
    // questions and disable the "Get my packet" button if there are any incomplete
    checkCompleteness();
  }, [probateDetails]);

  const checkCompleteness = (): ProbateField[] | undefined => {
    const init: ProbateField[] = [];
    const fields = probateDetails?.fieldsUsed?.reduce(
      (fields: ProbateField[], field: ProbateField) => {
        const haveAnswer = field.answerValue !== undefined;
        if (haveAnswer || field?.isComputed) {
          // if we have an answer or the field is computer on the backend don't add
          return fields;
        } else {
          // add to incomplete array
          return [...fields, field];
        }
      },
      init
    );
    // highlight incomplete fields
    setIncompleteFields(fields);
    return fields;
  };

  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const formErrors: string[] = [];
    const fields = checkCompleteness();
    const legalCheckbox = window.document.getElementsByName(
      "confirmationCheckbox"
    )[0] as HTMLInputElement;
    if (!legalCheckbox?.checked) {
      formErrors.push(
        "Please confirm that the answers provided are accurate to the best of your knowledge."
      );
      setConfirmationError(true);
    }
    if (fields?.length) {
      // display error message near continue and highlight fields
      formErrors.push(
        "Please provide and review all of the required information before continuing."
      );
    }
    if (formErrors?.length > 0) {
      setErrors(formErrors);
    } else {
      // We will go to the assembly screen first
      // After the assmebly screen, we will redirect to the signup screen on app1 with some packet data in the url
      // After signup, app1 will redirect to the forms and files page
      const afterUpgradeUrl = ATTICUS_CONSUMER_APP_HOST + SCREEN_PATHS.forms
      const leadDataCountryId = leadData && leadData["country_id"] ? leadData["country_id"] : -1
      const countryName = countries?.find(c => c.id === leadDataCountryId)?.fullName
      const leadDataStateId = leadData && leadData["state_id"] ? leadData["state_id"] : -1
      const stateName = states?.find(s => s.id === leadDataStateId)?.fullName

      const fieldsForUpgradePage = encodeProbateQueryParams(
        "",
        probateDetails?.probateType?.name || "Probate packet",
        probateDetails?.probateType?.numProbateForms || 0,
        countryName || "",
        stateName || "",
      )

      const afterAssemblyUrl = ATTICUS_CONSUMER_APP_HOST_ORIGINAL +
        "/home?show_upgrade=true&redirectToAfterUpgrade=" + encodeURIComponent(afterUpgradeUrl) +
        "&probate_data=" + encodeURIComponent(fieldsForUpgradePage)
      navigate(SCREEN_PATHS.leadProbateAssemble + `?signup=true&lead_forms=true&redirect_to=${encodeURIComponent(afterAssemblyUrl)}`);
    }
  };

  const getCategoryCards = (
    categoriesWithFields?: ProbateFieldCategory[]
  ): JSX.Element[] | JSX.Element | undefined => {
    return categoriesWithFields?.map((category: ProbateFieldCategory) => {
      const title = category?.title;
      return category?.fields ? (
        <React.Fragment key={category.id}>
          <Typography sx={{ mt: 4, mb: 1 }}>{title}</Typography>
          {getCategoryCard(title, category.fields)}
        </React.Fragment>
      ) : (
        <React.Fragment key={category.id} />
      );
    });
  };

  const getCategoryCard = (
    title: string,
    fields: ProbateField[]
  ): JSX.Element => {
    return (
      <Box
        sx={{
          my: 2,
          p: 2,
          backgroundColor: (theme: Theme) => theme.palette.background.paper,
        }}
      >
        {fields?.map((field: ProbateField, index: number) => {
          const highlight = !!incompleteFields?.find(
            (incompleteField: ProbateField) => incompleteField.id === field.id
          );
          // console.log("field?.id", field?.id);
          return !field?.isComputed ? (
            <RenderEditableField
              first={index === 0}
              key={field?.id}
              field={field}
              highlight={highlight}
              setQuestionComponent={(component?: JSX.Element) => {
                setErrors(undefined);
                // displays question in modal
                setQuestionComponent(component);
              }}
              onAnswer={async (answer: string | number | boolean) => {
                try {
                  field.answerValue = answer;
                  lead?.key && await probateApi.saveFieldToKnowledgeEngineForLead(lead.key, field);
                  saveFieldAnswered && saveFieldAnswered(field);
                } catch (error) {
                  console.error(error);
                  setErrors(
                    "Sorry, saving your answer failed.  Please try again"
                  );
                }
                // hide modal
                setQuestionComponent(undefined);
                // refresh probate details
                await getProbateDetails?.();
                // refresh completeness
                checkCompleteness();
              }}
            />
          ) : null;
        })}
      </Box>
    );
  };

  return (
    <ScreenContentWrapper
      pageSuffix="Probate"
      title="Get your Probate packet"
      step={8}
      totalSteps={10}
      showBeta={true}
      style={{ padding: "1rem 3rem 1rem 3rem" }}
    >
      {!lead || !probateDetails || !states || !countries || !leadData ? (
        <><Loading /></>
      ) : (
        <>
          <Typography variant="h5" sx={{ my: 3 }}>
            Before we show you your Probate Packet, let’s confirm everything
            you’ve told us so far
          </Typography>

          {getCategoryCards(probateDetails?.categoriesWithFields)}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
            }}
          >
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <label>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-around",
                      mb: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Checkbox
                      name="confirmationCheckbox"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setErrors(undefined);
                        setConfirmationError(!event.target.checked);
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "center", pt: 1 }}
                      color={confirmationError ? "error" : "default"}
                    >
                      I am confirming that the answers to the questions provided
                      above are accurate to the best of my knowledge and affirm
                      that Atticus is not providing any form of legal advice or
                      services.
                    </Typography>
                  </Box>
                </label>
                {errors &&
                  (errors.constructor === Array
                    ? errors?.map((error: string) => {
                      return (
                        <Typography color="error" sx={{ my: 2 }} key={error}>
                          {error}
                        </Typography>
                      );
                    })
                    : errors)}
                <Button
                  variant="contained"
                  type="submit"
                  disabled={incompleteFields && incompleteFields.length > 0}
                >
                  Get my packet
                </Button>
              </Box>
            </form>
          </Box>
          {/* Modal window to display the field question form */}
          <FormDialog
            open={questionComponent !== undefined}
            content={questionComponent}
            confirmText="Save"
            onConfirm={() => setQuestionComponent(undefined)}
          />
        </>
      )}
    </ScreenContentWrapper>
  );
};

export function encodeProbateQueryParams(leadKey: string, probateName: string, numForms: number, countryName: string, stateName: string): string {
  const vals = JSON.stringify({
    probateName,
    leadKey,
    numForms,
    countryName,
    stateName,
  })
  return encodeURIComponent(btoa(vals))
}

export function decodeProbateQueryParams(encodedVals: string): { probateName: string, leadKey: string, numForms: number, countryName: string, stateName: string } {
  return JSON.parse(atob(decodeURIComponent(encodedVals)))
}